import {
  deriveDailyTotals,
  padDailyTotals,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import { getHolidaySpans } from "@/v2-backoffice-client/usage/usage.seasons"

/**
 * Trailing window for the trendline — 7 days flattens the weekday/weekend
 * sawtooth that makes raw daily bars hard to read a direction from.
 */
export const TREND_WINDOW_DAYS = 7

const TOOLTIP_AREA_LIMIT = 3

export type UsageActivityChartPoint = {
  day: string
  value: number
  rawCount: number
  /** Trailing 7-day mean of the weighted total — the trendline. */
  trend: number
  /** Weighted value per app code for the stacked bars ("app:v1-console"). */
  [appValue: `app:${string}`]:
    | number
    | string
    | { area: string; value: number }[]
    | undefined
  topAreas: { area: string; value: number }[]
}

export type UsageActivityHolidayBand = {
  key: string
  label: string
  x1: string
  x2: string
}

export type DeriveUsageActivityChartPointsInput = {
  areaDays: UsageAreaDayRow[]
  /** Products the stacked bars split by (getAreaDayApps of the rows). */
  apps: string[]
  /** Days the window pads to, ending on `end` (today when omitted). */
  days: number
  end?: Date
}

/**
 * One point per day of the window: the weighted total, the raw count, the
 * per-product split for the stacked bars, the day's leading use cases for
 * the tooltip, and the trailing mean for the trendline. Quiet days are
 * zero-filled so they render as gaps instead of being skipped.
 */
export function deriveUsageActivityChartPoints(
  input: DeriveUsageActivityChartPointsInput,
): UsageActivityChartPoint[] {
  const { areaDays, apps, days, end } = input
  const totals = padDailyTotals(deriveDailyTotals(areaDays), days, end)

  const areasByDay = new Map<string, Map<string, number>>()
  const appsByDay = new Map<string, Map<string, number>>()
  for (const areaDay of areaDays) {
    const day = String(areaDay.Date).slice(0, 10)
    const value = areaDay.WeightedCount ?? areaDay.EventCount

    const areas = areasByDay.get(day) ?? new Map<string, number>()
    areas.set(areaDay.AppArea, (areas.get(areaDay.AppArea) ?? 0) + value)
    areasByDay.set(day, areas)

    if (areaDay.AppCode != null) {
      const dayApps = appsByDay.get(day) ?? new Map<string, number>()
      dayApps.set(areaDay.AppCode, (dayApps.get(areaDay.AppCode) ?? 0) + value)
      appsByDay.set(day, dayApps)
    }
  }

  const points = totals.map((entry) => {
    const point: UsageActivityChartPoint = {
      day: entry.day,
      value: Math.round(entry.value),
      rawCount: entry.rawCount,
      trend: 0,
      topAreas: [...(areasByDay.get(entry.day) ?? new Map())]
        .map(([area, value]) => ({ area, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, TOOLTIP_AREA_LIMIT),
    }
    for (const app of apps) {
      point[`app:${app}`] = Math.round(appsByDay.get(entry.day)?.get(app) ?? 0)
    }
    return point
  })

  // Trailing mean over the days seen so far (shorter at the window's
  // start), so the line starts at day one instead of appearing mid-chart.
  let rollingSum = 0
  for (const [index, point] of points.entries()) {
    rollingSum += point.value
    if (index >= TREND_WINDOW_DAYS) {
      rollingSum -= points[index - TREND_WINDOW_DAYS].value
    }
    point.trend = Math.round(
      rollingSum / Math.min(index + 1, TREND_WINDOW_DAYS),
    )
  }
  return points
}

/**
 * Norwegian vacation spans inside the visible window, as x-ranges to shade
 * on the chart so a July/Easter/Christmas dip reads as "everyone's away",
 * not churn. A one-day sliver has no room to read as a band and is dropped.
 */
export function deriveUsageActivityHolidayBands(
  points: UsageActivityChartPoint[],
): UsageActivityHolidayBand[] {
  if (points.length < 2) return []
  const first = new Date(points[0].day)
  const last = new Date(points[points.length - 1].day)
  return getHolidaySpans(first, last).flatMap((span) => {
    const inSpan = points.filter((point) => {
      const day = new Date(point.day)
      return day >= span.from && day <= span.to
    })
    if (inSpan.length < 2) return []
    return [
      {
        key: span.key,
        label: span.label,
        x1: inSpan[0].day,
        x2: inSpan[inSpan.length - 1].day,
      },
    ]
  })
}
